import React from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { ArrowLeft, Mail, Phone, GraduationCap, Briefcase } from "lucide-react";
import { STAFF } from "../mock";

const StaffDetail = () => {
  const { slug } = useParams();
  const person = STAFF.find((s) => s.slug === slug);

  if (!person) {
    return <Navigate to="/attorneys" replace />;
  }

  return (
    <div className="bg-white">
      {/* Header block */}
      <section className="bg-nw-navy text-white relative overflow-hidden">
        <div
          className="absolute inset-0 opacity-10"
          style={{
            backgroundImage:
              "radial-gradient(circle at 80% 20%, #b18f5f 0%, transparent 50%)",
          }}
        />
        <div className="container-nw relative py-12 md:py-16">
          <Link
            to="/attorneys"
            className="inline-flex items-center gap-2 text-white/70 hover:text-nw-gold-light text-xs uppercase tracking-[0.2em] mb-8"
          >
            <ArrowLeft className="w-4 h-4" /> Back to Our Team
          </Link>

          <div className="grid md:grid-cols-12 gap-10 items-end">
            <div className="md:col-span-4">
              <div className="relative aspect-[4/5] overflow-hidden bg-nw-navy-light shadow-2xl">
                <img
                  src={person.photo}
                  alt={`${person.firstName} ${person.lastName}`}
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </div>
            <div className="md:col-span-8">
              <div className="text-nw-gold-light text-xs uppercase tracking-[0.4em] mb-4">
                {person.title}
              </div>
              <h1 className="font-serif-display text-5xl md:text-6xl lg:text-7xl leading-tight">
                {person.firstName} {person.lastName}
              </h1>
              <div className="h-0.5 w-20 bg-nw-gold mt-6 mb-6" />
              <div className="flex flex-wrap gap-2 mb-8">
                {person.focus.map((f) => (
                  <span
                    key={f}
                    className="text-xs text-white/90 border border-white/25 bg-white/5 px-3 py-1.5"
                  >
                    {f}
                  </span>
                ))}
              </div>
              <div className="flex flex-wrap gap-6 text-sm">
                <a
                  href={`mailto:${person.email}`}
                  className="flex items-center gap-2 text-white/85 hover:text-nw-gold-light"
                >
                  <Mail className="w-4 h-4 text-nw-gold" />
                  {person.email}
                </a>
                <a
                  href={`tel:${person.phone}`}
                  className="flex items-center gap-2 text-white/85 hover:text-nw-gold-light"
                >
                  <Phone className="w-4 h-4 text-nw-gold" />
                  {person.phone}
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Body */}
      <section className="py-16 md:py-20">
        <div className="container-nw grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-8">
            <div className="text-nw-gold text-xs uppercase tracking-[0.3em] mb-3">
              Biography
            </div>
            <h2 className="font-serif-display text-3xl md:text-4xl text-nw-navy mb-2">
              About {person.firstName}
            </h2>
            <div className="h-0.5 w-14 bg-nw-gold mb-8" />
            <div className="prose prose-lg max-w-none space-y-5 text-nw-navy/80 leading-relaxed">
              {person.bio.map((p, i) => (
                <p key={i} className="text-base md:text-[17px] leading-[1.75]">
                  {p}
                </p>
              ))}
            </div>
          </div>

          <aside className="lg:col-span-4 space-y-8">
            <div className="bg-nw-cream p-7 border-t-2 border-nw-gold">
              <div className="flex items-center gap-3 mb-4">
                <Briefcase
                  className="w-5 h-5 text-nw-gold"
                  strokeWidth={1.5}
                />
                <h3 className="font-serif-display text-xl text-nw-navy">
                  Focus Areas
                </h3>
              </div>
              <ul className="space-y-2.5 text-sm text-nw-navy/80">
                {person.focus.map((c) => (
                  <li key={c} className="flex gap-2">
                    <span className="text-nw-gold">&mdash;</span>
                    <span>{c}</span>
                  </li>
                ))}
              </ul>
            </div>

            {person.education && person.education.length > 0 && (
              <div className="bg-nw-cream p-7 border-t-2 border-nw-gold">
                <div className="flex items-center gap-3 mb-4">
                  <GraduationCap
                    className="w-5 h-5 text-nw-gold"
                    strokeWidth={1.5}
                  />
                  <h3 className="font-serif-display text-xl text-nw-navy">
                    Education
                  </h3>
                </div>
                <ul className="space-y-2.5 text-sm text-nw-navy/80">
                  {person.education.map((c) => (
                    <li key={c} className="flex gap-2">
                      <span className="text-nw-gold">&mdash;</span>
                      <span>{c}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </aside>
        </div>
      </section>
    </div>
  );
};

export default StaffDetail;
